import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-smoke-50 via-flame-50 to-smoke-100">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-flame-600 hover:text-flame-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Calculator
        </Link>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-smoke-900 mb-6">Privacy Policy</h1>
          
          <div className="prose prose-sm max-w-none space-y-6 text-smoke-700">
            <p className="text-sm text-smoke-500">
              <strong>Effective Date:</strong> February 7, 2026
            </p>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Our Commitment to Privacy</h2>
              <p>
                BBQLabs.ai ("we," "our," or "us") is operated by Escape Velocity Ventures. We are committed 
                to protecting your privacy. This Privacy Policy explains how we handle information when you 
                use our BBQ cook time calculator at bbqlabs.ai.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">We Collect No Personal Data</h2>
              <p>
                <strong>BBQLabs.ai does not collect, store, or transmit any personal information.</strong> All 
                calculations are performed locally in your browser. We do not have a backend database, user 
                accounts, or any mechanism to store your data.
              </p>
              <ul className="list-disc pl-6 space-y-2 my-4">
                <li>No email addresses collected</li>
                <li>No user accounts or authentication</li>
                <li>No cookies for tracking or analytics</li>
                <li>No personal information stored on our servers</li>
                <li>All calculations performed client-side in your browser</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Weather Data</h2>
              <p>
                When you enter a location for weather-adjusted calculations, we query public weather APIs 
                (National Weather Service and Open-Meteo) on your behalf. These queries:
              </p>
              <ul className="list-disc pl-6 space-y-2 my-4">
                <li>Are made directly from your browser</li>
                <li>Do not pass through our servers</li>
                <li>May be subject to the privacy policies of those weather services</li>
                <li>Do not include any personal identifying information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Third-Party Services</h2>
              <p>
                We use the following third-party services:
              </p>
              <ul className="list-disc pl-6 space-y-2 my-4">
                <li><strong>Cloudflare Pages:</strong> Our hosting provider. Cloudflare may collect standard 
                    server logs (IP addresses, timestamps) for security and performance purposes. See 
                    Cloudflare's Privacy Policy.</li>
                <li><strong>National Weather Service API:</strong> U.S. government weather data (no personal 
                    data collected).</li>
                <li><strong>Open-Meteo API:</strong> Open-source weather data (no personal data collected).</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Browser Local Storage</h2>
              <p>
                Your browser may temporarily cache your calculator inputs (smoker, meat, weights, etc.) 
                using local browser storage. This data:
              </p>
              <ul className="list-disc pl-6 space-y-2 my-4">
                <li>Stays only on your device</li>
                <li>Is never transmitted to us</li>
                <li>Can be cleared by clearing your browser data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Security</h2>
              <p>
                Because we collect no personal data, there is no personal data to secure. All calculations 
                occur in your browser, and we cannot access your inputs or results.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Children's Privacy</h2>
              <p>
                Our service is not directed to children under 13. We do not knowingly collect information 
                from children. Since we collect no personal data from anyone, this protection extends to all users.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Any changes will be posted on this page 
                with an updated "Effective Date."
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy, please contact:
              </p>
              <p className="mt-2">
                <strong>Escape Velocity Ventures</strong><br />
                Email: privacy@escapevelo.com
              </p>
            </section>

            <div className="border-t border-smoke-200 mt-8 pt-6">
              <p className="text-sm text-smoke-600">
                BBQLabs.ai is a product of Escape Velocity Ventures. We build tools that respect your privacy 
                and give you control over your data.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
